import { ArrowRight, PlayCircle } from "lucide-react";
import { useEffect, useState } from "react";
import LedBackdrop from "@/components/LedBackdrop";

const HERO_IMG =
  "https://customer-assets-lxgj4vgw.emergentagent.net/job_led-web-platform/artifacts/ausckviy_image%203.png";

const HEADLINES = [
  { lines: ["Built to", "Be "], accent: "Seen." },
  { lines: ["Premium Displays.", "Powerful "], accent: "Presence." },
  { lines: ["Where Brands", ""], accent: "Stand Out." },
];
const HEADLINE_ROTATE_MS = 6000;

export const Hero = () => {
  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  const [headlineIndex, setHeadlineIndex] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setHeadlineIndex((i) => (i + 1) % HEADLINES.length), HEADLINE_ROTATE_MS);
    return () => clearInterval(t);
  }, []);

  const h = HEADLINES[headlineIndex];

  return (
    <section
      data-testid="hero-section"
      className="relative pt-32 lg:pt-40 pb-20 lg:pb-28 bg-white overflow-hidden"
    >
      {/* Animated LED pixel backdrop */}
      <LedBackdrop />

      {/* Display imagery in background */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden>
        <img
          src="https://customer-assets-lxgj4vgw.emergentagent.net/job_led-web-platform/artifacts/ausckviy_image%203.png"
          alt=""
          className="absolute -top-24 -right-32 w-[55%] max-w-[820px] aspect-[4/3] object-cover opacity-[0.10] grayscale"
          style={{
            WebkitMaskImage: "radial-gradient(circle at 70% 30%, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.5) 40%, transparent 78%)",
            maskImage: "radial-gradient(circle at 70% 30%, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.5) 40%, transparent 78%)",
          }}
        />
        <img
          src="https://customer-assets-lxgj4vgw.emergentagent.net/job_led-web-platform/artifacts/tbvpn2zh_image%204.png"
          alt=""
          className="absolute -bottom-32 -left-32 w-[52%] max-w-[720px] aspect-[3/2] object-cover opacity-[0.09] grayscale"
          style={{
            WebkitMaskImage: "radial-gradient(circle at 30% 60%, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.45) 40%, transparent 72%)",
            maskImage: "radial-gradient(circle at 30% 60%, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.45) 40%, transparent 72%)",
          }}
        />
        <img
          src="https://images.unsplash.com/photo-1763888450540-9b59abff803b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxtYWNybyUyMGxlZCUyMHBpeGVscyUyMHNjcmVlbnxlbnwwfHx8fDE3Nzg2NTMwOTN8MA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute top-1/3 -right-32 w-[40%] max-w-[520px] aspect-square object-cover opacity-[0.05] grayscale"
          style={{
            WebkitMaskImage: "radial-gradient(circle at center, rgba(0,0,0,0.8) 0%, transparent 65%)",
            maskImage: "radial-gradient(circle at center, rgba(0,0,0,0.8) 0%, transparent 65%)",
          }}
        />
      </div>

      {/* Single refined wash */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            "radial-gradient(1200px circle at 90% 0%, rgba(0,67,156,0.06), transparent 55%), radial-gradient(900px circle at 0% 100%, rgba(168,137,88,0.05), transparent 60%)",
        }}
        aria-hidden
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-12 lg:gap-14 items-center">
        <div className="lg:col-span-7">
          <h1
            key={headlineIndex}
            data-testid="hero-headline"
            className="headline-swap font-display font-extrabold text-[#0B1220] text-4xl sm:text-5xl lg:text-[3.5rem] xl:text-[4.5rem] leading-[1.02] tracking-tight"
          >
            <span className="block whitespace-nowrap">{h.lines[0]}</span>
            <span className="block whitespace-nowrap">
              {h.lines[1]}
              <span className="text-[#00439C]">{h.accent}</span>
            </span>
          </h1>

          <p
            className="mt-8 text-lg text-[#4B5563] max-w-2xl leading-relaxed rise"
            style={{ animationDelay: "0.15s" }}
            data-testid="hero-subtext"
          >
            From iconic outdoor LED displays to immersive indoor video walls, Smart Digi
            creates premium digital display solutions that combine cutting-edge technology,
            flawless engineering, and timeless design.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-3 rise" style={{ animationDelay: "0.22s" }}>
            <button
              data-testid="hero-cta-primary"
              onClick={() => go("contact")}
              className="group inline-flex items-center gap-2 px-7 py-3.5 bg-[#0B1220] text-white text-[15px] font-semibold rounded-md hover:bg-[#00439C] transition-colors"
            >
              Request a Quote <ArrowRight size={16} className="group-hover:translate-x-0.5 transition" />
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => go("showcase")}
              className="group inline-flex items-center gap-2 px-2 py-3.5 text-[#0B1220] text-[15px] font-semibold hover:text-[#00439C] transition"
            >
              <PlayCircle size={18} className="text-[#00439C]" /> See our work
            </button>
          </div>

          <div className="mt-14 pt-8 border-t hairline grid grid-cols-2 gap-8 max-w-lg">
            <Mini value="500+" label="Installations" />
            <Mini value="12 yrs" label="Industry experience" />
          </div>
        </div>

        <div className="lg:col-span-5 rise" style={{ animationDelay: "0.3s" }}>
          <div className="relative lg:scale-[1.06] lg:origin-right">
            {/* Ambient blue glow behind */}
            <div
              aria-hidden
              className="absolute -inset-8 -z-10 rounded-[2.5rem] bg-[radial-gradient(closest-side,rgba(0,67,156,0.35),rgba(0,67,156,0)_75%)] blur-2xl hero-glow"
            />
            <div className="relative rounded-2xl overflow-hidden border hairline shadow-[0_30px_80px_-30px_rgba(11,18,32,0.28)] hero-float group/hero">
              <img
                src={HERO_IMG}
                alt="LED display installation"
                className="w-full aspect-[4/5] object-cover"
              />
              {/* LED pixel-grid overlay */}
              <div aria-hidden className="hero-pixels absolute inset-0 pointer-events-none mix-blend-overlay" />
              {/* Diagonal shine sweep */}
              <div aria-hidden className="hero-shine absolute inset-0 pointer-events-none" />
              {/* Corner framing accents */}
              <span aria-hidden className="hero-corner top-3 left-3 border-t-2 border-l-2" />
              <span aria-hidden className="hero-corner top-3 right-3 border-t-2 border-r-2" />
              <span aria-hidden className="hero-corner bottom-3 left-3 border-b-2 border-l-2" />
              <span aria-hidden className="hero-corner bottom-3 right-3 border-b-2 border-r-2" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Mini = ({ value, label }) => (
  <div>
    <div className="font-display font-extrabold text-3xl lg:text-4xl leading-none tracking-tight text-[#0B1220]">{value}</div>
    <div className="mt-2.5 text-[12px] uppercase tracking-[0.14em] text-[#6B7280] font-semibold">{label}</div>
  </div>
);

export default Hero;
